import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  type SxProps,
  type Theme,
} from '@mui/material';

import { SortableOverviewList } from './SortableOverviewList';

import type { StoredStandard, UUID } from '@/shared/types/customStandard';
import type { OverviewItem } from '@/shared/types/overview';
import { newOverviewStandardsItem, newOverviewTextItem } from '@/shared/utils/contentItems';
import type { ReorderPlacement } from '@/shared/utils/reorder';

interface OverviewCardProps {
  title: string;
  entityName: string;
  items: OverviewItem[];
  canEdit: boolean;
  isUpdating: boolean;
  onSave: (items: OverviewItem[]) => Promise<void>;
  onMove: (itemId: string, placement: ReorderPlacement) => Promise<void>;
  ownerId?: UUID;
  parentScope?: StoredStandard[];
  allowAllToggle?: boolean;
  scopeLabel?: string;
  sx?: SxProps<Theme>;
}

export const OverviewCard: React.FC<OverviewCardProps> = ({
  title,
  entityName,
  items,
  canEdit,
  isUpdating,
  onSave,
  onMove,
  ownerId,
  parentScope,
  allowAllToggle,
  scopeLabel,
  sx,
}) => {
  const addItem = (item: OverviewItem) => {
    if (!canEdit || isUpdating) return;
    void onSave([...items, item]).catch((error) => {
      console.error(`Failed to add ${entityName} overview item:`, error);
    });
  };

  const updateItem = async (updatedItem: OverviewItem) => {
    if (isUpdating) return;
    if (!canEdit) throw new Error(`You do not have permission to edit this ${entityName}.`);
    await onSave(items.map((item) => (item.id === updatedItem.id ? updatedItem : item)));
  };

  const deleteItem = async (index: number) => {
    if (isUpdating) return;
    if (!canEdit) throw new Error(`You do not have permission to edit this ${entityName}.`);
    await onSave(items.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <Card sx={sx}>
      <CardHeader
        title={title}
        sx={{
          py: 1,
          '&& .MuiCardHeader-title': {
            fontSize: '1.5rem',
            lineHeight: 1.3,
          },
        }}
      />
      <CardContent sx={{ py: 0 }}>
        <SortableOverviewList
          items={items}
          canEdit={canEdit}
          isUpdating={isUpdating}
          onUpdate={updateItem}
          onDelete={deleteItem}
          onMove={onMove}
          ownerId={ownerId}
          parentScope={parentScope}
          allowAllToggle={allowAllToggle}
          scopeLabel={scopeLabel}
        />
      </CardContent>
      {canEdit && (
        <CardActions sx={{ justifyContent: 'center', pt: 0.25 }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => addItem(newOverviewTextItem())}
              disabled={isUpdating}
            >
              Add Text Item
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => addItem(newOverviewStandardsItem())}
              disabled={isUpdating}
            >
              Add Standards Item
            </Button>
          </Stack>
        </CardActions>
      )}
    </Card>
  );
};
